import * as React from 'react';
import { connectHits, connectStateResults } from 'react-instantsearch-core';
import Media from 'react-media';

import URI from 'jsuri';
import { compose } from 'recompose';

import type UserAgentInfo from 'js/lib/useragent';

import { ArrowNextIcon } from '@coursera/cds-icons';

import DesktopSearchCard from 'bundles/coursera-plus-common/components/search/DesktopSearchCard';
import MobileSearchCard from 'bundles/coursera-plus-common/components/search/MobileSearchCard';
import SearchCardsPlaceHolder from 'bundles/coursera-ui/components/search/SearchCardsPlaceHolder';
import type { Hit } from 'bundles/internal-search/types';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { TABLET_SCREEN_WIDTH } from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { getNumberOfSearchResults } from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/coursera-plus';

import 'css!./__styles__/InPageAlgoliaSearchResults';

export type InputProps = {
  userAgent: UserAgentInfo;
  isSearchDelayOn: boolean;
  currentSearchQuery: string;
  numberOfResults: number;
  currentTab: string;
  removeLinks?: boolean;
  showCourseraLitePill?: boolean;
};

type Props = {
  hits: Array<Hit>;
  searchResults: { _state: { query: string }; queryID: string } | null;
  searching: boolean;
  searchState: { query: string };
  allSearchResults: Record<string, $TSFixMe>;
} & InputProps;

type PlaceholderProps = {
  searching: boolean;
  searchState: { query: string };
  searchResults: { _state: { query: string } } | null;
  isSearchDelayOn: boolean;
  numberOfResults: number;
  children: React.ReactNode;
};

// We use this to render a placeholder while the results are being fetched
// Reference: https://community.algolia.com/react-instantsearch/connectors/connectStateResults.html
const SearchPlaceHolderWrapper = ({
  searching,
  searchState,
  searchResults,
  isSearchDelayOn,
  numberOfResults,
  children,
}: PlaceholderProps) => {
  const newQuery = searchState?.query;
  // Use searchResults._state.query since query mapping rules set up on Algolia dashboard can change searchResults.query
  const currentResultsQuery = searchResults?._state?.query;
  const isQueryMatching = newQuery === currentResultsQuery;

  // If isQueryMatching === false (meaning: the new query sent to Algolia doesn't match the query for current results)
  // we simply show a spinner indicating we are fetching new data for a new query.
  // The reason why "searching" boolean isn't enough is that it takes a request to Algolia servers to receive that boolean, and
  // by the time it comes back there is already a lag on user experience & the new data may be already available, so we want to show
  // the spinner as soon as possible
  return isQueryMatching && !isSearchDelayOn && !searching ? (
    <div>{children}</div>
  ) : (
    <SearchCardsPlaceHolder count={numberOfResults} />
  );
};

const SearchResultsComponent = ({
  userAgent,
  hits,
  isSearchDelayOn,
  searchResults,
  searching,
  currentSearchQuery,
  searchState,
  numberOfResults,
  currentTab,
  allSearchResults,
  removeLinks = false,
  showCourseraLitePill = false,
}: Props) => {
  const currentNumberOfHits = getNumberOfSearchResults(currentTab, allSearchResults);

  const refLink = new URI('/search')
    .addQueryParam('query', currentSearchQuery)
    .addQueryParam(IS_PART_OF_COURSERA_PLUS, true)
    .toString();

  return (
    <div className="rc-AlgoliaSearchResults">
      <SearchPlaceHolderWrapper
        isSearchDelayOn={isSearchDelayOn}
        searchResults={searchResults}
        searching={searching}
        searchState={searchState}
        numberOfResults={numberOfResults}
      >
        <div className="ais-InfiniteHits">
          <ul className="ais-InfiniteHits-list">
            {hits.map((hit, hitPosition) => (
              <li key={hit.objectID} className="ais-InfiniteHits-item">
                <Media query={{ maxWidth: TABLET_SCREEN_WIDTH }} defaultMatches={userAgent?.isMobileBrowser}>
                  {(matches) => {
                    const SearchCardComponent = matches ? MobileSearchCard : DesktopSearchCard;
                    return (
                      <SearchCardComponent
                        hit={hit}
                        hitPosition={hitPosition}
                        queryID={searchResults?.queryID}
                        showCourseraLitePill={showCourseraLitePill}
                        removeLinks={removeLinks}
                      />
                    );
                  }}
                </Media>
              </li>
            ))}
          </ul>
        </div>
      </SearchPlaceHolderWrapper>
      {currentNumberOfHits > 0 && !removeLinks && (
        <TrackedLink2
          data={{ searchQuery: currentSearchQuery }}
          withVisibilityTracking={true}
          requireFullyVisible={true}
          href={refLink}
          target="_blank"
          rel="noopener noreferrer"
          trackingName="view_all_coursera_plus_results_search"
          className="link-wrapper"
        >
          <h3 className="view-all-link">{_t('See more results')}</h3>
          <ArrowNextIcon color="interactive" size="small" style={{ transform: 'translateY(3px)', fontSize: '10px' }} />
        </TrackedLink2>
      )}
    </div>
  );
};

// Reference: https://community.algolia.com/react-instantsearch/connectors/connectStateResults.html
// The wrapped searchResultsComponent will receive prop searchResults through the connectStateResults Connection
// As required by Algolia's click analytics, queryId in searchResults should be collected
// Reference to old search's queryId collection: static/bundles/search-common/legacy-algolia/CustomInfiniteHits.jsx
export default compose<Props, InputProps>(connectHits, connectStateResults)(SearchResultsComponent);
